import React from 'react';
import { StyleSheet, View } from 'react-native';

import { DateInput } from '@/components/form/date-input';
import { Button } from '@/components/ui';
import { businessDaysAgoRange, monthToDate, yearToDate } from '@/utils/report-dates';

interface DateRangeSelectorProps {
	label?: string;
	startDate: Date | null;
	endDate: Date | null;
	onStartDateChange: (date: Date | null) => void;
	onEndDateChange: (date: Date | null) => void;
	style?: any;
	/**
	 * The range is business dates (#447): both pickers honour `DateInput`'s `utcDay` contract, and
	 * the quick ranges come from the ET helpers instead of `new Date()` and local getters.
	 */
	utcDay?: boolean;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
	label,
	startDate,
	endDate,
	onStartDateChange,
	onEndDateChange,
	style,
	utcDay = false,
}) => {
	// Helper functions for quick date options
	const getLast7Days = () => {
		const today = new Date();
		const sevenDaysAgo = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));
		return { start: sevenDaysAgo, end: today };
	};

	const getPreviousWeek = () => {
		const today = new Date();
		const lastWeekEnd = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));
		const lastWeekStart = new Date(lastWeekEnd.getTime() - (6 * 24 * 60 * 60 * 1000));
		return { start: lastWeekStart, end: lastWeekEnd };
	};

	const getMonthToDate = () => {
		const today = new Date();
		const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
		return { start: monthStart, end: today };
	};

	const getLast30Days = () => {
		const today = new Date();
		const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
		return { start: thirtyDaysAgo, end: today };
	};

	const getYearToDate = () => {
		const today = new Date();
		const yearStart = new Date(today.getFullYear(), 0, 1);
		return { start: yearStart, end: today };
	};

	const quick = utcDay
		? {
			last7Days: () => businessDaysAgoRange(7),
			previousWeek: () => businessDaysAgoRange(13, 7),
			monthToDate: () => monthToDate(),
			last30Days: () => businessDaysAgoRange(30),
			yearToDate: () => yearToDate(),
		}
		: {
			last7Days: getLast7Days,
			previousWeek: getPreviousWeek,
			monthToDate: getMonthToDate,
			last30Days: getLast30Days,
			yearToDate: getYearToDate,
		};

	const applyQuickRange = (dates: { start: Date; end: Date }) => {
		onStartDateChange(dates.start);
		onEndDateChange(dates.end);
	};

	return (
		<View style={[styles.container, style]}>
			<View style={styles.dateInputs}>
				<DateInput
					label="Start date"
					value={startDate}
					onChange={onStartDateChange}
					style={styles.dateInput}
					utcDay={utcDay}
				/>
				<DateInput
					label="End date"
					value={endDate}
					onChange={onEndDateChange}
					style={styles.dateInput}
					utcDay={utcDay}
				/>
			</View>
			<View style={styles.quickOptions}>
				<Button
					mode="text"
					compact
					onPress={() => applyQuickRange(quick.last7Days())}
					style={styles.quickOption}
				>
					Last 7 Days
				</Button>
				<Button
					mode="text"
					compact
					onPress={() => applyQuickRange(quick.previousWeek())}
					style={styles.quickOption}
				>
					Previous Week
				</Button>
				<Button
					mode="text"
					compact
					onPress={() => applyQuickRange(quick.monthToDate())}
					style={styles.quickOption}
				>
					Month-to-date
				</Button>
				<Button
					mode="text"
					compact
					onPress={() => applyQuickRange(quick.last30Days())}
					style={styles.quickOption}
				>
					Last 30 Days
				</Button>
				<Button
					mode="text"
					compact
					onPress={() => applyQuickRange(quick.yearToDate())}
					style={styles.quickOption}
				>
					Year-to-date
				</Button>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		gap: 12,
	},
	dateInputs: {
		flexDirection: 'row',
		gap: 12,
		flexWrap: 'wrap',
	},
	dateInput: {
		minWidth: 180,
		flex: 1,
	},
	quickOptions: {
		flexDirection: 'row',
		gap: 8,
		flexWrap: 'wrap',
		marginTop: 4,
	},
	quickOption: {
		paddingHorizontal: 8,
		paddingVertical: 4,
		minHeight: 28,
	},
});

export default DateRangeSelector;
